import { getProductById } from '@/service/v3/endpoints/products'
import type { Product } from '@/types'
import type { V3Response } from '@/types/V3UtilityTypes'
import { useSWR } from '@attensi/swr'
import { computed, unref } from 'vue'
import type { MaybeRef } from '../usePolicyTypeSelection'
import { useOnError } from './helpers/useOnError'
import { useCurrentCompany } from './useCurrentCompany'

const getProductOrUndefined = async (
  productId: string | undefined
): V3Response<Product> => {
  if (productId === undefined) return undefined
  return getProductById(productId) as V3Response<Product>
}

export const useProduct = (productId: MaybeRef<string | undefined>) => {
  const productParams = [productId, 'get-product']
  const {
    data: product,
    error,
    mutate: mutateProduct,
  } = useSWR(productParams, getProductOrUndefined)

  useOnError(error)

  const isLoadingProduct = computed(() => {
    return !product.value && unref(productId)
  })

  const unwrappedProduct = computed(() => {
    if (!product.value) return
    return {
      id: product.value.data.id,
      ...product.value.data.attributes,
    }
  })

  const { data: currentCompany } = useCurrentCompany()

  const isSharedToCompany = computed(() => {
    return (
      unwrappedProduct.value?.shared &&
      unwrappedProduct.value?.company_id !== currentCompany.value?.id
    )
  })

  return {
    product,
    unwrappedProduct,
    isLoadingProduct,
    error,
    mutateProduct,
    isSharedToCompany,
  }
}
